import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const THEME = {
  impressions: "#1e3a5f",
  impressionsSoft: "#3b82c4",
  clicks: "#0f766e",
  clicksSoft: "#2dd4bf",
  grid: "#f1f5f9",
  axis: "#94a3b8",
  spent: "#b45309",
  remaining: "#0f766e",
};

const shortLabel = (title, index, max = 12) => {
  const base = String(title || `Ad ${index + 1}`).trim() || `Ad ${index + 1}`;
  const label = base.length > max ? `${base.slice(0, max - 1)}…` : base;
  return label;
};

const formatNum = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString();
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const full =
    payload[0]?.payload?.fullName ||
    payload[0]?.payload?.name ||
    label;
  return (
    <div className="ads-chart-tooltip">
      <div className="ads-chart-tooltip__label">{full}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="ads-chart-tooltip__row">
          <span
            className="ads-chart-tooltip__dot"
            style={{ background: entry.color || entry.stroke || entry.fill }}
          />
          <span className="ads-chart-tooltip__name">{entry.name}</span>
          <strong>
            {entry.dataKey === "ctr"
              ? `${entry.value}%`
              : Number(entry.value).toLocaleString()}
          </strong>
        </div>
      ))}
    </div>
  );
};

const LegendPills = ({ items }) => (
  <div className="ads-chart-legend" aria-hidden>
    {items.map((item) => (
      <span key={item.label} className="ads-chart-legend__item">
        <span
          className="ads-chart-legend__swatch"
          style={{ background: item.color }}
        />
        {item.label}
      </span>
    ))}
  </div>
);

const EmptyChart = ({ message = "No performance data yet" }) => (
  <div className="ads-chart-empty">
    <span className="ads-chart-empty__title">Nothing to chart</span>
    <span className="ads-chart-empty__msg">{message}</span>
  </div>
);

const AdvertiserAnalyticsCharts = ({ ads = [], adMetrics = {} }) => {
  const chartData = useMemo(() => {
    const titleCounts = {};
    (Array.isArray(ads) ? ads : []).forEach((ad) => {
      const t = String(ad?.title || ad?.description || "Ad").trim() || "Ad";
      titleCounts[t] = (titleCounts[t] || 0) + 1;
    });
    const seen = {};

    return (Array.isArray(ads) ? ads : []).map((ad, i) => {
      const impressions = Number(ad?.impressions) || 0;
      const clicks = Number(ad?.clicks) || 0;
      const ctr =
        impressions > 0
          ? Number(((clicks / impressions) * 100).toFixed(2))
          : 0;
      const rawTitle = ad?.title || ad?.description || `Ad ${i + 1}`;
      const title = String(rawTitle).trim() || `Ad ${i + 1}`;
      let name = shortLabel(title, i);
      if (titleCounts[title] > 1) {
        seen[title] = (seen[title] || 0) + 1;
        name = shortLabel(`${title} ${seen[title]}`, i);
      }
      return {
        key: ad?._id || ad?.ad_id || `ad-${i}`,
        name,
        fullName: title,
        impressions,
        clicks,
        ctr,
      };
    });
  }, [ads]);

  const totals = useMemo(() => {
    const impressions = Number(adMetrics?.impressions) || 0;
    const clicks = Number(adMetrics?.clicks) || 0;
    const ctr =
      impressions > 0
        ? Number(((clicks / impressions) * 100).toFixed(2))
        : Number(adMetrics?.ctr) || 0;
    return { impressions, clicks, ctr };
  }, [adMetrics]);

  const engagementData = useMemo(() => {
    let imp = 0;
    let clk = 0;
    return chartData.map((row) => {
      imp += row.impressions;
      clk += row.clicks;
      return {
        name: row.name,
        fullName: row.fullName,
        impressions: imp,
        clicks: clk,
      };
    });
  }, [chartData]);

  const budgetPie = useMemo(() => {
    const spent = Number(adMetrics?.spent) || 0;
    const total = Number(adMetrics?.totalBudget) || 0;
    const remaining =
      total > 0
        ? Math.max(Math.round((total - spent) * 100) / 100, 0)
        : Math.max(Number(adMetrics?.remainingBudget) || 0, 0);
    return [
      { name: "Spent", value: Number(spent.toFixed(2)) },
      { name: "Remaining", value: Number(remaining.toFixed(2)) },
    ].filter((s) => s.value > 0);
  }, [adMetrics]);

  const budgetTotals = useMemo(() => {
    const spent = Number(adMetrics?.spent) || 0;
    const total = Number(adMetrics?.totalBudget) || 0;
    const remaining =
      total > 0
        ? Math.max(Math.round((total - spent) * 100) / 100, 0)
        : Math.max(Number(adMetrics?.remainingBudget) || 0, 0);
    return {
      spent: Number(spent.toFixed(2)),
      remaining: Number(remaining.toFixed(2)),
      total: Number((total > 0 ? total : spent + remaining).toFixed(2)),
    };
  }, [adMetrics]);

  const hasData = chartData.length > 0;
  const hasReach = chartData.some((d) => d.impressions > 0 || d.clicks > 0);

  return (
    <div className="ads-charts">
      <div className="ads-charts__metrics">
        <div className="ads-metric">
          <span className="ads-metric__label">Impressions</span>
          <span className="ads-metric__value">{formatNum(totals.impressions)}</span>
        </div>
        <div className="ads-metric">
          <span className="ads-metric__label">Clicks</span>
          <span className="ads-metric__value">{formatNum(totals.clicks)}</span>
        </div>
        <div className="ads-metric">
          <span className="ads-metric__label">CTR</span>
          <span className="ads-metric__value">{totals.ctr}%</span>
        </div>
        <div className="ads-metric">
          <span className="ads-metric__label">Campaigns</span>
          <span className="ads-metric__value">{chartData.length}</span>
        </div>
      </div>

      <div className="ads-charts__stack">
        {/* Impressions & Clicks */}
        <div className="ads-chart-panel">
          <div className="ads-chart-panel__head">
            <div>
              <h4 className="ads-chart-panel__title">Impressions &amp; Clicks</h4>
              <p className="ads-chart-panel__sub">
                Performance comparison by campaign
              </p>
            </div>
            <LegendPills
              items={[
                { label: "Impressions", color: THEME.impressions },
                { label: "Clicks", color: THEME.clicks },
              ]}
            />
          </div>
          <div className="ads-chart-panel__body ads-chart-panel__body--tall">
            {!hasData || !hasReach ? (
              <EmptyChart message="Impressions and clicks will appear once ads are served." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 12, right: 12, left: 0, bottom: 4 }}
                  barCategoryGap="28%"
                  barGap={6}
                >
                  <CartesianGrid
                    stroke={THEME.grid}
                    vertical={false}
                    strokeDasharray="0"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: THEME.axis, fontSize: 12, fontWeight: 500 }}
                    dy={6}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: THEME.axis, fontSize: 11 }}
                    allowDecimals={false}
                    width={44}
                    tickFormatter={formatNum}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "rgba(30, 58, 95, 0.04)" }}
                  />
                  <Bar
                    dataKey="impressions"
                    name="Impressions"
                    fill={THEME.impressions}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={40}
                  />
                  <Bar
                    dataKey="clicks"
                    name="Clicks"
                    fill={THEME.clicks}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Engagement trend */}
        <div className="ads-chart-panel">
          <div className="ads-chart-panel__head">
            <div>
              <h4 className="ads-chart-panel__title">Engagement trend</h4>
              <p className="ads-chart-panel__sub">
                Cumulative reach and engagement across campaigns
              </p>
            </div>
            <LegendPills
              items={[
                { label: "Impressions", color: THEME.impressionsSoft },
                { label: "Clicks", color: THEME.clicks },
              ]}
            />
          </div>
          <div className="ads-chart-panel__body ads-chart-panel__body--tall">
            {!hasData || !hasReach ? (
              <EmptyChart message="Engagement trend builds as your ads collect traffic." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={engagementData}
                  margin={{ top: 12, right: 12, left: 0, bottom: 4 }}
                >
                  <defs>
                    <linearGradient id="engImp" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={THEME.impressionsSoft}
                        stopOpacity={0.28}
                      />
                      <stop
                        offset="100%"
                        stopColor={THEME.impressionsSoft}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient id="engClk" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={THEME.clicks}
                        stopOpacity={0.22}
                      />
                      <stop
                        offset="100%"
                        stopColor={THEME.clicks}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke={THEME.grid}
                    vertical={false}
                    strokeDasharray="0"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: THEME.axis, fontSize: 12, fontWeight: 500 }}
                    dy={6}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: THEME.axis, fontSize: 11 }}
                    allowDecimals={false}
                    width={44}
                    tickFormatter={formatNum}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{
                      stroke: "#cbd5e1",
                      strokeWidth: 1,
                      strokeDasharray: "4 4",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="impressions"
                    name="Impressions"
                    stroke={THEME.impressionsSoft}
                    fill="url(#engImp)"
                    strokeWidth={2.5}
                    activeDot={{
                      r: 5,
                      stroke: "#fff",
                      strokeWidth: 2,
                      fill: THEME.impressionsSoft,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="clicks"
                    name="Clicks"
                    stroke={THEME.clicks}
                    fill="url(#engClk)"
                    strokeWidth={2.5}
                    activeDot={{
                      r: 5,
                      stroke: "#fff",
                      strokeWidth: 2,
                      fill: THEME.clicks,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Compact budget strip (kept, secondary) */}
        {budgetPie.length > 0 ? (
          <div className="ads-chart-panel ads-chart-panel--budget">
            <div className="ads-chart-panel__head">
              <div>
                <h4 className="ads-chart-panel__title">Budget</h4>
                <p className="ads-chart-panel__sub">Spent vs remaining</p>
              </div>
            </div>
            <div className="ads-budget-row">
              <div className="ads-budget-chart">
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie
                      data={budgetPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={38}
                      outerRadius={52}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {budgetPie.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={
                            entry.name === "Spent"
                              ? THEME.spent
                              : THEME.remaining
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        `$${Number(value).toFixed(2)}`,
                        "",
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="ads-budget-stats">
                <div className="ads-budget-stat">
                  <span
                    className="ads-chart-legend__swatch"
                    style={{ background: THEME.impressions }}
                  />
                  <div>
                    <div className="ads-budget-stat__label">Total</div>
                    <div className="ads-budget-stat__value">
                      ${budgetTotals.total.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="ads-budget-stat">
                  <span
                    className="ads-chart-legend__swatch"
                    style={{ background: THEME.spent }}
                  />
                  <div>
                    <div className="ads-budget-stat__label">Spent</div>
                    <div className="ads-budget-stat__value">
                      ${budgetTotals.spent.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="ads-budget-stat">
                  <span
                    className="ads-chart-legend__swatch"
                    style={{ background: THEME.remaining }}
                  />
                  <div>
                    <div className="ads-budget-stat__label">Remaining</div>
                    <div className="ads-budget-stat__value">
                      ${budgetTotals.remaining.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdvertiserAnalyticsCharts;
